import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Input } from "@/components/ui/Input";
import { shouldIgnoreOpeningPointer } from "@/lib/touchOpenClose";
import { useTouchListPickSession } from "@/lib/useTouchListPickSession";
import { cn } from "@/lib/utils";
import { computeAnchoredListPanel, FLOATING_POPOVER_Z_INDEX } from "@/lib/viewportAnchoredPopover";

/** Floor / tablet fields: 16px so iOS does not zoom-and-blur, and a larger tap target. */
export const FLOOR_TEXT_INPUT_CLASS = "h-12 text-base";

const LIST_MAX_HEIGHT_PX = 280;

function normalizeCompactName(name: string): string {
  return name.toLowerCase().replace(/[\s\-]+/g, "");
}

function tokenizeSuggestionName(name: string): string[] {
  return name
    .toLowerCase()
    .split(/[\s\-]+/)
    .filter((token) => token !== "");
}

export function filterFreeTextSuggestions(suggestions: readonly string[], query: string): string[] {
  const needle = query.trim().toLowerCase();
  const deduped = [...new Set(suggestions)];
  if (needle.length === 0) {
    return deduped;
  }

  const compactNeedle = normalizeCompactName(needle);
  return deduped.filter((name) => {
    const lower = name.toLowerCase();
    const tokens = tokenizeSuggestionName(name);
    const firstToken = tokens[0] ?? "";
    const compact = normalizeCompactName(name);

    if (firstToken.startsWith(needle)) return true;
    if (tokens.some((token, index) => index > 0 && token.startsWith(needle) && needle.length >= 4)) {
      return true;
    }
    if (compactNeedle.length >= 4 && compact.includes(compactNeedle)) return true;
    if (needle.length >= 4 && lower.includes(needle)) return true;
    return false;
  });
}

export function FreeTextSuggestionInput({
  id,
  value,
  onChange,
  suggestions,
  placeholder,
  disabled = false,
  className,
  inputClassName,
  autoComplete = "off",
  required = false,
  onCommit,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  suggestions: readonly string[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Classes on the text field. When set, replaces the default floor height. */
  inputClassName?: string;
  autoComplete?: string;
  required?: boolean;
  /** Called with the field text on blur, Enter, or a picked suggestion. */
  onCommit?: (value: string) => void;
}): JSX.Element {
  const listboxId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const openGuardRef = useRef<{ openedAtMs: number; pointerId: number | null }>({
    openedAtMs: 0,
    pointerId: null,
  });
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [panelBox, setPanelBox] = useState<ReturnType<typeof computeAnchoredListPanel> | null>(null);

  const matches = useMemo(() => filterFreeTextSuggestions(suggestions, value), [suggestions, value]);
  const showPanel = open && !disabled && matches.length > 0;
  const touchListPick = useTouchListPickSession(showPanel);

  const repositionPanel = (): void => {
    const anchor = rootRef.current;
    if (anchor == null) {
      setPanelBox(null);
      return;
    }
    setPanelBox(
      computeAnchoredListPanel({
        anchorRect: anchor.getBoundingClientRect(),
        listMaxHeightCap: LIST_MAX_HEIGHT_PX,
        minWidth: anchor.offsetWidth,
      }),
    );
  };

  useLayoutEffect(() => {
    if (!showPanel) {
      setPanelBox(null);
      return;
    }
    repositionPanel();
    const onLayout = (): void => {
      repositionPanel();
    };
    window.addEventListener("resize", onLayout);
    window.addEventListener("scroll", onLayout, true);
    window.visualViewport?.addEventListener("resize", onLayout);
    window.visualViewport?.addEventListener("scroll", onLayout);
    return () => {
      window.removeEventListener("resize", onLayout);
      window.removeEventListener("scroll", onLayout, true);
      window.visualViewport?.removeEventListener("resize", onLayout);
      window.visualViewport?.removeEventListener("scroll", onLayout);
    };
  }, [showPanel, matches.length, value]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (rootRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }
      if (
        shouldIgnoreOpeningPointer({
          openedAtMs: openGuardRef.current.openedAtMs,
          openingPointerId: openGuardRef.current.pointerId,
          nowMs: Date.now(),
          eventPointerId: event.pointerId,
          eventPointerType: event.pointerType,
        })
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [value, showPanel]);

  function pickSuggestion(name: string): void {
    onChange(name);
    setOpen(false);
    onCommit?.(name);
  }

  const portalTarget = typeof document !== "undefined" ? document.body : null;
  const panel =
    showPanel && panelBox != null && portalTarget != null
      ? createPortal(
          <div
            ref={panelRef}
            id={listboxId}
            role="listbox"
            data-floating-combobox-panel=""
            className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-lg"
            style={{
              position: "fixed",
              top: panelBox.top,
              left: panelBox.left,
              width: panelBox.width,
              zIndex: FLOATING_POPOVER_Z_INDEX,
            }}
          >
            <ul
              className="overflow-y-auto overscroll-y-contain py-1 touch-pan-y [-webkit-overflow-scrolling:touch]"
              style={{ maxHeight: panelBox.listContentMaxHeight }}
            >
              {matches.map((name, index) => (
                <li key={name} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={highlightedIndex === index}
                    className={cn(
                      "flex min-h-12 w-full items-center px-3 text-left text-base touch-manipulation hover:bg-slate-50",
                      highlightedIndex === index && "bg-slate-50",
                    )}
                    onPointerDown={(event) => {
                      touchListPick.onListItemPointerDown(event, () => {
                        pickSuggestion(name);
                      });
                    }}
                  >
                    {name}
                  </button>
                </li>
              ))}
            </ul>
          </div>,
          portalTarget,
        )
      : null;

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <Input
        id={id}
        type="text"
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete={autoComplete}
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        required={required}
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showPanel}
        aria-controls={showPanel ? listboxId : undefined}
        className={cn(inputClassName ?? FLOOR_TEXT_INPUT_CLASS, "touch-manipulation")}
        onPointerDown={(event) => {
          if (disabled) return;
          openGuardRef.current = { openedAtMs: Date.now(), pointerId: event.pointerId };
        }}
        onFocus={() => {
          if (disabled) return;
          openGuardRef.current = { openedAtMs: Date.now(), pointerId: null };
          setOpen(true);
        }}
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
        }}
        onBlur={(event) => {
          if (touchListPick.shouldYieldBlurCommit()) return;
          onCommit?.(event.currentTarget.value);
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.stopPropagation();
            setOpen(false);
            return;
          }
          if (!showPanel) {
            if (event.key === "Enter") {
              event.preventDefault();
              onCommit?.(value);
            }
            return;
          }
          if (event.key === "ArrowDown") {
            event.preventDefault();
            setHighlightedIndex((current) => Math.min(current + 1, matches.length - 1));
            return;
          }
          if (event.key === "ArrowUp") {
            event.preventDefault();
            setHighlightedIndex((current) => Math.max(current - 1, 0));
            return;
          }
          if (event.key === "Enter" && matches[highlightedIndex] != null) {
            event.preventDefault();
            pickSuggestion(matches[highlightedIndex] ?? "");
          }
        }}
      />
      {panel}
    </div>
  );
}
